<script setup lang="ts">
definePageMeta({
  layout: 'documents'
})
</script>

<template>
  <section class="section">
    <h1 class="headline-2 title">Privacy policy</h1>

    <div class="paragraf top-paragraf">
      <p class="date font-links">
        Last update: <data class="text-m" value="2024-12-27">27.12.2024</data>
      </p>
      <p class="font-bodytext">
        Infinity Bytes Software Design L.L.C Privacy Policy
      </p>
      <p class="font-bodytext">
        This Privacy Policy describes how Switchcase.dev (“we,” “us,” or “our”)
        collects, uses, and discloses your information when you use our website,
        <nuxt-link class="link" to="https://www.switchcase.dev"
          >www.switchcase.dev</nuxt-link
        >
        (the “Platform”).
      </p>
    </div>

    <div class="paragraf">
      <h2 class="font-title paragraf-title">
        1. Collection of your personal data
      </h2>
      <p class="paragraf-text font-bodytext">
        When you fill out the contact form on our Platform, we collect only the
        following personal information:
      </p>
      <ul class="list">
        <li class="list-item">
          <icon
            class="list-item-bullet"
            size="22px"
            name="icons:products-bullet"
          />Name
        </li>
        <li class="list-item">
          <icon
            class="list-item-bullet"
            size="22px"
            name="icons:products-bullet"
          />Email address
        </li>
      </ul>
    </div>

    <div class="paragraf">
      <h2 class="font-title paragraf-title">2. How we collect information</h2>
      <p class="paragraf-text font-bodytext">
        We collect the above information directly from you when you submit it
        through the contact form on our Platform.
      </p>
    </div>

    <div class="paragraf">
      <h2 class="font-title paragraf-title">3. How we use your information</h2>
      <p class="paragraf-text font-bodytext">
        We use the collected information to:
      </p>
      <ul class="list">
        <li class="list-item font-bodytext">
          <icon
            class="list-item-bullet"
            size="22px"
            name="icons:products-bullet"
          />Respond to your inquiries and messages
        </li>
        <li class="list-item font-bodytext">
          <icon
            class="list-item-bullet"
            size="22px"
            name="icons:products-bullet"
          />Provide information about our services and updates (with your
          consent)
        </li>
      </ul>
    </div>

    <div class="paragraf">
      <h2 class="font-title paragraf-title">
        4. Disclosure of your information
      </h2>
      <p class="paragraf-text font-bodytext">
        We do not share your personal information with third parties, except in
        the following cases:
      </p>
      <ul class="list">
        <li class="list-item font-bodytext">
          <icon
            class="list-item-bullet"
            size="22px"
            name="icons:products-bullet"
          />When required by law or in response to government requests
        </li>
        <li class="list-item font-bodytext">
          <icon
            class="list-item-bullet"
            size="22px"
            name="icons:products-bullet"
          /><span
            >When necessary to protect the rights, property, or safety of
            <nuxt-link class="link" to="https://www.switchcase.dev"
              >Switchcase.dev</nuxt-link
            >, our users, or others</span
          >
        </li>
      </ul>
    </div>

    <div class="paragraf">
      <h2 class="font-title paragraf-title">5. Data security</h2>
      <p class="paragraf-text font-bodytext">
        We take measures to protect your information from unauthorized access,
        use, or disclosure. However, transmission of data over the Internet
        cannot be guaranteed as completely secure, and we cannot ensure absolute
        security during transmission.
      </p>
    </div>

    <div class="paragraf">
      <h2 class="font-title paragraf-title">6. Data retention</h2>
      <p class="paragraf-text font-bodytext">
        We retain your information only for as long as necessary to fulfill the
        purposes for which it was collected, or as required by applicable law.
      </p>
    </div>

    <div class="paragraf">
      <h2 class="font-title paragraf-title">7. Your rights</h2>
      <p class="paragraf-text font-bodytext">
        Depending on your jurisdiction, you may have the right to request access
        to, correction, or deletion of your personal information. To exercise
        these rights, please contact us using the details below.
      </p>
    </div>

    <div class="paragraf">
      <h2 class="font-title paragraf-title">8. Changes to this policy</h2>
      <p class="paragraf-text font-bodytext">
        We may update this Privacy Policy from time to time. Any changes will be
        posted on this page.
      </p>
    </div>

    <div class="paragraf">
      <h2 class="font-title paragraf-title">9. Contact information</h2>
      <p class="paragraf-text font-bodytext">
        For questions regarding this Privacy Policy and our data practices,
        please contact us at:
      </p>

      <p class="paragraf-text font-bodytext">
        <nuxt-link class="link font-bodytext" to="mailto:info@switchcase.dev"
          >info@switchcase.dev</nuxt-link
        >
      </p>
    </div>
  </section>
</template>

<style scoped>
.section {
  padding: 132px 0 120px;
  width: 100%;
  max-width: 660px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 40px;
}

.title {
  text-transform: uppercase;
}

.paragraf {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.paragraf-title {
  margin-bottom: 24px;
}

.paragraf-text {
  margin-bottom: 15px;
}

.paragraf-text:last-child {
  margin-bottom: 0;
}

.top-paragraf {
  gap: 16px;
}

.date {
  text-transform: uppercase;
}

.link {
  color: var(--main-blue);
  text-decoration: none;
}

.list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
}

.list-item-bullet {
  filter: blur(0.7px);
  flex-shrink: 0;
}

.list-item {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
}

@media (max-width: 1200px) {
  .section {
    max-width: 756px;
  }
}

@media (max-width: 1023px) {
  .section {
    gap: 32px;
    padding: 112px 20px 60px;
  }

  .top-paragraf {
    gap: 12px;
  }

  .paragraf-title {
    margin-bottom: 20px;
  }

  .paragraf-text {
    margin-bottom: 10px;
  }

  .list {
    gap: 2px;
  }
}

@media (max-width: 743px) {
  .paragraf-text {
    margin-bottom: 12px;
  }
}
</style>
